import React, { useState, useEffect, useCallback } from 'react'
import api from '../services/api'
import { useIsMobile } from '../hooks/useIsMobile'
import toast from 'react-hot-toast'
import { Search, Trash2, Eye, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, Database, Rows4 } from 'lucide-react'
import { format, parseISO } from 'date-fns'

function fmtDate(val) {
  if (!val) return '—'
  try { return format(parseISO(val), 'dd/MM/yy HH:mm') } catch { return val }
}

function renderCell(val) {
  if (val === null || val === undefined) return <span style={{ color: 'var(--text-muted)' }}>—</span>
  if (typeof val === 'boolean') {
    return <span className={`badge ${val ? 'badge-green' : 'badge-red'}`}>{val ? 'Yes' : 'No'}</span>
  }
  if (typeof val === 'object') return <span className="badge badge-gray">objeto</span>
  const s = String(val)
  return s.length > 38 ? <span title={s}>{s.slice(0, 36)}…</span> : s
}

// Etapa del pingtree como badge de color. El objetivo es que "Secondary
// fallback" (el respaldo final, sin precio mínimo) salte a la vista: si mucho
// volumen cae ahí, es señal de un problema de filtrado aguas arriba.
const PINGTREE_STAGE_STYLES = {
  'Auction':            { cls: 'badge-green', title: 'Sold in the auction' },
  'Exclusive window':   { cls: 'badge-blue',  title: 'Sold in the exclusive window' },
  'Partner route':      { cls: 'badge-blue',  title: 'Sold through a partner route' },
  'Secondary fallback': { cls: 'badge-amber', title: 'Fell through to the HighWinds safety net (no minimum price)' },
  'In auction':         { cls: 'badge-gray',  title: 'Auction still running' },
  'No buyer':           { cls: 'badge-gray',  title: 'No buyer took this lead' },
}

function renderPingtreeStage(val) {
  if (!val) return <span style={{ color: 'var(--text-muted)' }}>—</span>
  const s = PINGTREE_STAGE_STYLES[val] || { cls: 'badge-gray', title: val }
  return <span className={`badge ${s.cls}`} title={s.title}>{val}</span>
}

// Encabezados legibles para columnas cuyo nombre crudo no se entiende.
const COL_HEADERS = {
  partner_name:   'Source',
  pingtree_stage: 'Sold at',
  winner:         'Buyer',
}

const PRIORITY_COLS = [
  '_id', 'name', 'username', 'email', 'phone', 'cleanPhone',
  'lenderName', 'lenderIdentifier', 'identifier', 'status', 'statusStr',
  'success', 'isActive', 'role', 'aff_id', 'user', 'version',
  'clickid', 'transaction_id', 'event', 'source', 'campaignID',
  'leadId', 'reason', 'redirectUrl',
  'createdAt', 'updatedAt',
]

const SKIP_COLS = [
  '__v', 'leadData', 'lenderResponse', 'sentPayload', 'hyaliteResponse',
  'rawPayload', 'config', 'data', 'metadata', 'allowedLenders',
  'content', 'password', 'apiKey',
]

function getDisplayCols(data, col) {
  const isUnified = col === 'unified-leads'
  const seen = new Set()
  data.forEach((row) => Object.keys(row).forEach((k) => seen.add(k)))
  let cols = [...seen].filter((k) => !SKIP_COLS.includes(k))
  if (isUnified) {
    // En Unified Leads mostramos 'Source' (partner) en lugar del _id crudo,
    // y ocultamos lead_id (ya visible en el detalle de cada lead).
    cols = cols.filter((k) => k !== '_id' && k !== 'lead_id')
  }
  cols.sort((a, b) => {
    const ai = PRIORITY_COLS.indexOf(a)
    const bi = PRIORITY_COLS.indexOf(b)
    if (ai !== -1 && bi !== -1) return ai - bi
    if (ai !== -1) return -1
    if (bi !== -1) return 1
    return a.localeCompare(b)
  })
  cols = cols.slice(0, 8)
  if (isUnified) {
    // Orden fijo en Unified Leads: Source primero, y a continuación la
    // visibilidad del pingtree (dónde se vendió y a quién), que es lo que se
    // consulta a diario. `updatedAt` se omite: en esta colección repite
    // siempre el valor de `createdAt` y gastaba una de las 8 columnas.
    cols = cols.filter((k) => !['partner_name', 'pingtree_stage', 'winner', 'updatedAt'].includes(k))
    cols = ['partner_name', ...cols]
    // pingtree_stage y winner van al final, juntos, para que se lean como par.
    cols = cols.slice(0, 6)
    cols.push('pingtree_stage', 'winner')
  }
  return cols
}

// ── Tarjeta de documento (versión móvil de la fila) ────────────────────
function CollectionCard({ row, cols, onView, onDelete }) {
  const hasId = cols.includes('_id')
  const otherCols = cols.filter((c) => c !== '_id')

  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* _id + acciones */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          {hasId && (
            <>
              <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', fontWeight: 600 }}>
                _id
              </div>
              <div style={{
                fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)',
                wordBreak: 'break-all', marginTop: 2,
              }}>
                {row._id}
              </div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button className="btn btn-ghost btn-icon" title="View detail" onClick={onView} style={{ width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Eye size={20} />
          </button>
          <button className="btn btn-danger btn-icon" title="Delete" onClick={onDelete} style={{ width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trash2 size={20} />
          </button>
        </div>
      </div>

      {/* Resto de campos como pares clave–valor */}
      {otherCols.length > 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          paddingTop: hasId ? 8 : 0,
          borderTop: hasId ? '1px solid var(--border)' : 'none',
        }}>
          {otherCols.map((c) => (
            <div key={c} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
              <span style={{
                fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5,
                color: 'var(--text-muted)', fontWeight: 600, flexShrink: 0,
              }}>
                {COL_HEADERS[c] || c}
              </span>
              <span style={{ fontSize: 13, textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}>
                {c === 'createdAt' || c === 'updatedAt' ? fmtDate(row[c]) : c === 'pingtree_stage' ? renderPingtreeStage(row[c]) : renderCell(row[c])}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function DetailModal({ doc, onClose }) {
  const renderVal = (val, depth = 0) => {
    if (val === null || val === undefined) return <span style={{ color: 'var(--text-muted)' }}>null</span>
    if (typeof val === 'boolean') return <span className={`badge ${val ? 'badge-green' : 'badge-red'}`}>{String(val)}</span>
    if (typeof val === 'object' && !Array.isArray(val)) {
      return (
        <div style={{ marginLeft: depth > 0 ? 12 : 0 }}>
          {Object.entries(val).map(([k, v]) => (
            <div key={k} className="detail-row" style={{ paddingLeft: depth * 8 }}>
              <span className="detail-key">{k}</span>
              <span className="detail-val">{renderVal(v, depth + 1)}</span>
            </div>
          ))}
        </div>
      )
    }
    if (Array.isArray(val)) return <span style={{ color: 'var(--text-secondary)' }}>[{val.join(', ')}]</span>
    return <span>{String(val)}</span>
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h2>Document detail</h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {Object.entries(doc).map(([k, v]) => (
            <div key={k} className="detail-row">
              <span className="detail-key">{k}</span>
              <span className="detail-val">{renderVal(v)}</span>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

function ConfirmModal({ onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="modal-header"><h2>Confirm deletion</h2></div>
        <div className="modal-body">
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            Are you sure? This action cannot be undone.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger btn-sm" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  )
}

// Multi-select con chips: dropdown de opciones (checkbox) + tags removibles.
function MultiChipSelect({ label, options, selected, onChange, width }) {
  const [open, setOpen] = useState(false)
  const ref = React.useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val])
  const labelFor = (val) => (options.find(o => o.value === val)?.label) || val
  return (
    <div ref={ref} style={{ position: 'relative', minWidth: width || 190 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          width: '100%', padding: '9px 12px', fontSize: 14, borderRadius: 8, cursor: 'pointer',
          border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
        title={label}>
        <span style={{ color: selected.length ? 'var(--text-primary)' : 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selected.length ? `${label} · ${selected.length}` : label}
        </span>
        <ChevronDown size={15} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 9999, minWidth: '100%',
          maxHeight: 300, overflowY: 'auto', background: 'var(--bg-raised, var(--bg-surface))',
          border: '1px solid var(--border-strong)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', padding: 4 }}>
          {options.length === 0 && <div style={{ padding: 8, fontSize: 12, color: 'var(--text-muted)' }}>No options</div>}
          {options.map(o => (
            <label key={o.value} onMouseDown={(e) => e.preventDefault()}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', fontSize: 14,
                cursor: 'pointer', borderRadius: 4, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)}
                style={{ cursor: 'pointer', width: 15, height: 15 }} />
              {o.label}
            </label>
          ))}
        </div>
      )}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {selected.map(val => (
            <span key={val} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
              padding: '2px 6px', borderRadius: 999, background: 'var(--bg-raised, #1e293b)',
              border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
              {labelFor(val)}
              <span onClick={() => toggle(val)} style={{ cursor: 'pointer', fontWeight: 700, lineHeight: 1 }} title="Remove">×</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default function CollectionPage({ col, title }) {
  const isMobile = useIsMobile()
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [pages, setPages] = useState(1)
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState(null)
  const [confirm, setConfirm] = useState(null)

  // Filtros multi (solo unified-leads): source + winner, con chips + Apply.
  const isUnifiedView = col === 'unified-leads'
  const [filterOptions, setFilterOptions] = useState({ sources: [], winners: [] })
  const [pendingSources, setPendingSources] = useState([])
  const [pendingWinners, setPendingWinners] = useState([])
  const [appliedSources, setAppliedSources] = useState([])
  const [appliedWinners, setAppliedWinners] = useState([])

  const filtersDirty = (
    [...pendingSources].sort().join(',') !== [...appliedSources].sort().join(',') ||
    [...pendingWinners].sort().join(',') !== [...appliedWinners].sort().join(',')
  )

  const fetchData = useCallback(async (pg = 1, search = '', srcs = appliedSources, wins = appliedWinners) => {
    setLoading(true)
    try {
      const params = { page: pg, limit: 20, ...(search.trim() ? { q: search.trim() } : {}) }
      if (isUnifiedView) {
        if (srcs.length) params.sources = srcs.join(',')
        if (wins.length) params.winners = wins.join(',')
      }
      const { data: res } = await api.get(`/${col}`, { params })
      setData(res.data || [])
      setTotal(res.total || 0)
      setPages(res.pages || 1)
      setPage(pg)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error loading data')
    } finally {
      setLoading(false)
    }
  }, [col, isUnifiedView, appliedSources, appliedWinners])

  // Cargar opciones de filtro (solo unified-leads)
  useEffect(() => {
    if (!isUnifiedView) return
    let cancel = false
    api.get('/unified-leads-filters')
      .then(({ data }) => { if (!cancel) setFilterOptions({ sources: data.sources || [], winners: data.winners || [] }) })
      .catch(() => {})
    return () => { cancel = true }
  }, [isUnifiedView])

  const applyFilters = useCallback(() => {
    setAppliedSources(pendingSources)
    setAppliedWinners(pendingWinners)
    fetchData(1, q, pendingSources, pendingWinners)
  }, [pendingSources, pendingWinners, q, fetchData])

  useEffect(() => {
    setQ('')
    setPendingSources([]); setPendingWinners([])
    setAppliedSources([]); setAppliedWinners([])
    fetchData(1, '', [], [])
  }, [col]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    try {
      await api.delete(`/${col}/${id}`)
      toast.success('Eliminado correctamente')
      fetchData(page, q)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete')
    }
    setConfirm(null)
  }

  const cols = data.length > 0 ? getDisplayCols(data, col) : []

  // Paginación compartida entre la tabla (desktop) y las tarjetas (móvil).
  const paginationEl = (
    <div className="pagination" style={{ flexWrap: 'wrap', rowGap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 8 }}>
        Page {page} of {pages} · {total.toLocaleString()} records
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => fetchData(page - 1, q)} aria-label="Previous page">
        <ChevronLeft size={14} />
      </button>
      {Array.from({ length: Math.min(5, pages) }, (_, i) => {
        const p = Math.max(1, Math.min(pages - 4, page - 2)) + i
        return (
          <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => fetchData(p, q)} aria-label={`Page ${p}`} aria-current={p === page ? 'page' : undefined}>{p}</button>
        )
      })}
      <button className="page-btn" disabled={page >= pages} onClick={() => fetchData(page + 1, q)} aria-label="Next page">
        <ChevronRight size={14} />
      </button>
    </div>
  )

  return (
    <div className="fade-in page-shell">
      <section className="page-hero compact-hero">
        <div className="page-hero-content">
          <span className="hero-kicker">Collection workspace</span>
          <h1>{title}</h1>
          <p>Review live documents, search quickly, inspect details, and operate the collection without leaving the admin.</p>
          <div className="hero-chips">
            <span className="badge badge-blue"><Rows4 size={12} /> {total.toLocaleString()} records</span>
            <span className="badge badge-gray"><Database size={12} /> GET /core/{col}</span>
          </div>
        </div>
      </section>

      <section className="card section-card toolbar-card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', overflow: 'visible', position: 'relative', zIndex: 20 }}>
        <form onSubmit={(e) => { e.preventDefault(); fetchData(1, q) }} style={{ flex: 1, minWidth: isMobile ? '100%' : 220 }}>
          <div className="search-bar search-bar-elevated">
            <Search size={14} className="search-icon" />
            <input className="input" placeholder="Search records, ids, names or statuses…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </form>
        <button
          className="btn btn-ghost"
          onClick={() => fetchData(page, q)}
          disabled={loading}
          style={{ width: isMobile ? '100%' : undefined, justifyContent: isMobile ? 'center' : undefined }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 0.7s linear infinite' : 'none' }} />
          Refresh
        </button>

        {isUnifiedView && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start',
            width: isMobile ? '100%' : 'auto' }}>
            <MultiChipSelect
              label="All sources"
              options={filterOptions.sources}
              selected={pendingSources}
              onChange={setPendingSources}
              width={isMobile ? '100%' : 190}
            />
            <MultiChipSelect
              label="All winners"
              options={filterOptions.winners}
              selected={pendingWinners}
              onChange={setPendingWinners}
              width={isMobile ? '100%' : 180}
            />
            <button className="btn btn-primary" onClick={applyFilters} disabled={!filtersDirty}
              style={{ opacity: filtersDirty ? 1 : 0.5, width: isMobile ? '100%' : undefined,
                justifyContent: isMobile ? 'center' : undefined }}>
              Apply filters
            </button>
          </div>
        )}
      </section>

      {loading && data.length === 0 ? (
        <div className="page-shell-center"><div className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} /></div>
      ) : data.length === 0 ? (
        <div className="card empty-state section-card"><p>No results{q ? ` for "${q}"` : ''}</p></div>
      ) : isMobile ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.map((row) => (
              <CollectionCard
                key={row._id}
                row={row}
                cols={cols}
                onView={() => setDetail(row)}
                onDelete={() => setConfirm(row._id)}
              />
            ))}
          </div>
          {paginationEl}
        </>
      ) : (
        <section className="card section-card table-card-shell" style={{ position: 'relative', zIndex: 1 }}>
          <div className="table-wrap table-wrap-elevated">
            <table>
              <thead>
                <tr>
                  {cols.map((c) => <th key={c}>{COL_HEADERS[c] || c}</th>)}
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row._id}>
                    {cols.map((c) => (
                      <td
                        key={c}
                        style={{
                          fontFamily: c === '_id' ? 'monospace' : 'inherit',
                          fontSize: c === '_id' ? 11 : 13,
                          color: c === '_id' ? 'var(--text-muted)' : 'inherit',
                        }}
                      >
                        {c === 'createdAt' || c === 'updatedAt' ? fmtDate(row[c]) : c === 'pingtree_stage' ? renderPingtreeStage(row[c]) : renderCell(row[c])}
                      </td>
                    ))}
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-ghost btn-icon" title="View detail" onClick={() => setDetail(row)} style={{ width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Eye size={20} />
                        </button>
                        <button className="btn btn-danger btn-icon" title="Delete" onClick={() => setConfirm(row._id)} style={{ width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {paginationEl}
        </section>
      )}

      {detail ? <DetailModal doc={detail} onClose={() => setDetail(null)} /> : null}
      {confirm ? <ConfirmModal onConfirm={() => handleDelete(confirm)} onCancel={() => setConfirm(null)} /> : null}
    </div>
  )
}
